import React from 'react';
import { Box, TextField, FormControlLabel, Checkbox, Typography, Divider } from '@mui/material';
import { ProductSelect } from './selectors/ProductSelect';
import { BundleSelect } from './selectors/BundleSelect';
import { VariantSelect } from './selectors/VariantSelect';
import { SlotSelect } from './selectors/SlotSelect';
import { ModifierSelect } from './selectors/ModifierSelect';
import { WeightSelect } from './selectors/WeightSelect';
import { WeightPriceRanges } from './selectors/WeightPriceRanges';
interface OperationFormProps {
  fields: any;
  handlers: any;
  mode?: 'create' | 'edit' | 'remove';
  menu?: any;
}
export function OperationForm({ fields, handlers, mode = 'create' }: OperationFormProps) {
  
  // Create a wrapper function to update product with selected variant
  const handleVariantChange = (variants: any) => {
    // When a variant is selected, attach it to the product object so child components can access it
    if (fields.product && variants && variants.length > 0) {
      const selectedVariant = variants[0]; // Use the first selected variant
      
      // Create a new product object with the selected variant
      const updatedProduct = {
        ...fields.product,
        selectedVariant // Add this property to the product
      };
      
      // Update the product in the form state
      handlers.setProduct(updatedProduct);
      
      // Also update the variants in the form state
      handlers.setVariants(variants);
      
      // Clear dependent fields when variant changes
      handlers.setSlot(null);
      handlers.setModifier([]);
      handlers.setWeights([]);
    } else {
      // Just update variants if no product is selected
      handlers.setVariants(variants);
      
      // Clear dependent fields
      handlers.setSlot(null);
      handlers.setModifier([]);
      handlers.setWeights([]);
    }
  };
  
  // Handler to clear all related fields when product changes
  const handleProductChange = () => {
    // Clear variants, slot, modifiers, and weights when product changes
    handlers.setVariants([]);
    handlers.setSlot(null);
    handlers.setModifier([]);
    handlers.setWeights([]);
  };
  
  // Handler to clear dependent fields when slot changes
  const handleSlotChange = (slot: any) => {
    handlers.setSlot(slot);
    // Clear dependent fields when slot changes
    handlers.setModifier([]);
    handlers.setWeights([]);
  };const renderFormFields = () => {
    if (!fields.operationType) return null;
    // Common price field for operations that need it
    const renderPriceField = () => {
      // Don't render price field in remove mode
      if (mode === 'remove') {
        return null;
      }
      return (
        <TextField
          label='Price'
          type='number'
          value={fields.price}
          onChange={(e) => handlers.setPrice(e.target.value)}
          fullWidth
          margin='normal'
          InputProps={{
            startAdornment: <Typography variant='body2'>$</Typography>,
          }}
        />
      );
    };
    // Render fields based on operation type
    switch (fields.operationType) {
      case 'SetBundlePriceMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              Set Bundle Price
            </Typography>
            <BundleSelect value={fields.bundle} onChange={handlers.setBundle} multiple={true} />
            {renderPriceField()}
          </>
        );
      case 'SetVariantPriceMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              Set Variant Price
            </Typography>
            <ProductSelect value={fields.product} onChange={handlers.setProduct} onProductChange={handleProductChange} />
            <Box mt={2}>
              <VariantSelect
                value={fields.variants}
                onChange={handleVariantChange}
                product={fields.product}
                multiple={true}
              />
            </Box>
            {renderPriceField()}
          </>
        );
      case 'SetModifierWeightPriceMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              Set Modifier Weight Price
            </Typography>
            <ProductSelect value={fields.product} onChange={handlers.setProduct} onProductChange={handleProductChange} />
            <Box mt={2}>
              <VariantSelect
                value={fields.variants}
                onChange={handleVariantChange}
                product={fields.product}
                multiple={true}
              />
            </Box>
            <Box mt={2}>
              <SlotSelect 
                value={fields.slot} 
                onChange={handleSlotChange} 
                product={fields.product}
              />
            </Box>
            <Box mt={2}>
              <ModifierSelect
                value={fields.modifier}
                onChange={handlers.setModifier}
                product={fields.product}
                multiple={true}
              />
            </Box>
            <Box mt={2}>
              <WeightSelect 
                value={fields.weights} 
                onChange={handlers.setWeights} 
                product={fields.product}
                multiple={true} 
              />
            </Box>
            {renderPriceField()}
          </>
        );
      case 'SetModifierPriceRangesMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              Set Modifier Price Ranges
            </Typography>
            <ProductSelect value={fields.product} onChange={handlers.setProduct} onProductChange={handleProductChange} />
            <Box mt={2}>
              <VariantSelect
                value={fields.variants}
                onChange={handleVariantChange}
                product={fields.product}
                multiple={true}
              />
            </Box>
            <Box mt={2}>
              <SlotSelect value={fields.slot} onChange={handleSlotChange} product={fields.product} />
            </Box>
            {mode !== 'remove' && (
              <Box mt={2}>
                <WeightPriceRanges ranges={fields.ranges} onChange={handlers.setRanges} />
              </Box>
            )}
          </>
        );
      case 'RemoveProductMenuPatchOperation':
      case 'RemoveVariantMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              {fields.operationType === 'RemoveProductMenuPatchOperation' ? 'Remove Product' : 'Remove Variant'}
            </Typography>
            <ProductSelect
              value={fields.product}
              onChange={handlers.setProduct}
              multiple={fields.operationType === 'RemoveProductMenuPatchOperation'}
              onProductChange={handleProductChange}
            />
            {fields.operationType === 'RemoveVariantMenuPatchOperation' && (
              <Box mt={2}>
                <VariantSelect
                  value={fields.variants}
                  onChange={handleVariantChange}
                  product={fields.product}
                  multiple={true}
                />
              </Box>
            )}
          </>
        );
      case 'RemoveBundleMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              Remove Bundle
            </Typography>
            <BundleSelect value={fields.bundle} onChange={handlers.setBundle} multiple={true} />
          </>
        );
      case 'RemoveModifierFromSlotPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              Remove Modifier From Slot
            </Typography>
            <Box mt={2}>
              <SlotSelect value={fields.slot} onChange={handleSlotChange} menu={fields.menu} />
            </Box>
            <Box mt={2}>
              <ModifierSelect
                value={fields.modifier}
                onChange={handlers.setModifier}
                menu={fields.menu}
                multiple={true}
              />
            </Box>
          </>
        );
      case 'RemoveModifierMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              Remove Modifier
            </Typography>
            <Box mt={2}>
              <ModifierSelect
                value={fields.modifier}
                onChange={handlers.setModifier}
                menu={fields.menu}
                multiple={true}
              />
            </Box>
          </>
        );
      case 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation':
        return (
          <>
            <Typography variant='subtitle1' gutterBottom>
              {mode === 'remove' 
                ? 'Remove Modifier Weight Exclusion From Variant' 
                : 'Exclude Modifier Weight From Variant'
              }
            </Typography>
            <ProductSelect value={fields.product} onChange={handlers.setProduct} multiple={false} onProductChange={handleProductChange} />
            <Box mt={2}>
              <VariantSelect
                value={fields.variants}
                onChange={handleVariantChange}
                product={fields.product}
                multiple={true}
              />
            </Box>
            <Box mt={2}>
              <SlotSelect value={fields.slot} onChange={handleSlotChange} product={fields.product} />
            </Box>
            <Box mt={2}>
              <ModifierSelect
                value={fields.modifier}
                onChange={handlers.setModifier}
                product={fields.product}
                multiple={true}
              />
            </Box>
            <Box mt={2}>
              <WeightSelect value={fields.weights} onChange={handlers.setWeights} product={fields.product} multiple={true} />
            </Box>
            <Box mt={2}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fields.excludeFromVariant}
                    onChange={(e) => handlers.setExcludeFromVariant(e.target.checked)}
                  />
                }
                label='Exclude from variant'
              />
            </Box>
          </>
        );
      default:
        return <Typography color='textSecondary'>This operation type is not yet supported in the form.</Typography>;
    }
  };
  return (
    <Box sx={{ mt: 3 }} data-testid="operationForm">
      <Divider sx={{ mb: 3 }} />
      {renderFormFields()}
    </Box>
  );
}
